import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { OtpInputState } from '@voyagu/portal-design-system';
import { interval, map, Observable, Subject, takeUntil } from 'rxjs';

import { PortalUserVerificationNode } from '@app/services/api/api.types';
import { intervalMs } from '@app/utils/constants';
import { bindOtpDigitDataIds } from '@app/utils/otp-digit-data-ids.util';
import { getDomain } from '@app/utils/utils';

@Component({
  selector: 'activation-code-modal',
  templateUrl: './activation-code-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivationCodeModalComponent implements OnInit, OnChanges, AfterViewInit {
  hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  domain: { label: string; url: string } | null;
  form = new FormGroup({
    code: new FormControl('', { nonNullable: true }),
  });

  timer$: Observable<number>;

  loading: boolean;

  @Input() activation: PortalUserVerificationNode;
  @Input() activationPassed: boolean;
  @Input() activationError: string | null;

  @Output() hideModal = new EventEmitter();
  @Output() validateCode = new EventEmitter();
  @Output() sendActivationEmail = new EventEmitter();

  get otpState(): OtpInputState {
    if (this.activationPassed) {
      return 'success';
    }

    if (this.form.disabled) {
      return 'disabled';
    }

    return 'default';
  }

  ngOnInit() {
    this.domain = getDomain(this.activation?.emailMasked);
    this.startCountdown();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (typeof changes['activationError']?.currentValue === 'string') {
      this.form.enable();
      this.form.controls.code.setValue('');
      this.focusFirstInput();
    }

    if (changes['activationPassed']?.currentValue === true) {
      setTimeout(() => this.hideModal.emit(), 1000);
    }
  }

  ngAfterViewInit() {
    bindOtpDigitDataIds(this.hostElement.nativeElement, 'digit');
    this.focusFirstInput();
  }

  onCompleted(code: string) {
    if (this.form.disabled || code.length !== 6) {
      return;
    }

    this.form.disable();
    this.validateCode.emit(code);
  }

  sendVerificationEmail() {
    if (!this.loading) {
      this.sendActivationEmail.emit();
    }

    this.loading = true;
  }

  startCountdown() {
    const destroy$ = new Subject<void>();

    const endTime =
      new Date(`${this.activation.emailSentAt.split('+')[0]}+00:00`).getTime() + intervalMs; // todo fix after be

    this.timer$ = interval(1000)
      .pipe(takeUntil(destroy$))
      .pipe(
        map(() => {
          const now = new Date().getTime();

          const currentTime = endTime - now;

          if (currentTime <= 0) {
            setTimeout(() => {
              destroy$.next();
              destroy$.complete();
            });
          }

          return currentTime;
        }),
      );
  }

  focusFirstInput() {
    setTimeout(() => {
      bindOtpDigitDataIds(this.hostElement.nativeElement, 'digit');
      this.hostElement.nativeElement.querySelector<HTMLElement>('.otp-input__cell')?.focus();
    });
  }
}
